"use client"

import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Calendar, dateFnsLocalizer } from "react-big-calendar"
import { format, parse, startOfWeek, getDay } from "date-fns"
import { es } from "date-fns/locale"
import { motion, AnimatePresence } from "framer-motion"
import {
  CalendarDays,
  List as ListIcon,
  Plus,
  X,
  Building2,
  CreditCard,
  CircleDollarSign,
  CalendarClock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  Edit3,
  Mail,
  StickyNote,
  Bell,
} from "lucide-react"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "./Calendario.css"
import {
  getPagos,
  createPago,
  updatePago,
  updateEstadoPago,
  deletePago,
  revisarVencimientos,
  notificarPago,
  getEmpresas,
} from "../../services/pagoEmpresaService"

/* -------------------- Localizador del calendario (español) -------------------- */
const locales = { es }
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
})

/* -------------------- Helpers de estado -------------------- */
const ESTADOS = {
  PENDIENTE: { label: "Pendiente", color: "#f59e0b", icon: Clock },
  RENOVADO: { label: "Renovado", color: "#10b981", icon: CheckCircle2 },
  NO_RENOVO: { label: "No renovó", color: "#ef4444", icon: XCircle },
}

const estadoInfo = (estado) => ESTADOS[estado] || ESTADOS.PENDIENTE

/** Convierte "YYYY-MM-DD" a Date local (evita corrimiento por zona horaria). */
const toDate = (str) => {
  if (!str) return null
  const [y, m, d] = str.split("-").map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

const fmtFecha = (str) => {
  const d = toDate(str)
  if (!d) return "—"
  return d.toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" })
}

const fmtMoneda = (v) =>
  v == null ? "—" : new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD" }).format(v)

const hoyISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

/* ======================= Modal Crear / Editar ======================= */
const PagoModal = ({ pago, empresas, onClose, onSave }) => {
  const isEdit = pago !== null
  const [form, setForm] = useState({
    idEmpresa: "",
    plan: "",
    precio: "",
    fechaInicio: hoyISO(),
    fechaVencimiento: "",
    estado: "PENDIENTE",
    nota: "",
  })
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isEdit) {
      setForm({
        idEmpresa: pago.idEmpresa ?? "",
        plan: pago.plan ?? "",
        precio: pago.precio ?? "",
        fechaInicio: pago.fechaInicio ?? hoyISO(),
        fechaVencimiento: pago.fechaVencimiento ?? "",
        estado: pago.estado ?? "PENDIENTE",
        nota: pago.nota ?? "",
      })
    }
  }, [pago, isEdit])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((p) => ({ ...p, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    if (!form.idEmpresa) return setError("Selecciona una empresa.")
    if (!form.fechaVencimiento) return setError("La fecha de vencimiento es obligatoria.")
    if (form.fechaInicio && form.fechaVencimiento && form.fechaVencimiento < form.fechaInicio) {
      return setError("La fecha de vencimiento no puede ser anterior a la de inicio.")
    }

    const payload = {
      idEmpresa: Number(form.idEmpresa),
      plan: form.plan.trim(),
      precio: form.precio === "" ? null : Number(form.precio),
      fechaInicio: form.fechaInicio || null,
      fechaVencimiento: form.fechaVencimiento,
      estado: form.estado,
      nota: form.nota.trim(),
    }

    try {
      setSaving(true)
      await onSave(payload, isEdit ? pago.id : null)
    } catch (err) {
      setError(err.message || "No se pudo guardar.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <motion.div
        className="cal-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
      >
        <div className="cal-modal-header">
          <h3>
            <CreditCard size={20} />
            {isEdit ? "Editar pago" : "Nuevo pago"}
          </h3>
          <button className="cal-icon-btn" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="cal-form">
          <label className="cal-field">
            <span><Building2 size={15} /> Empresa</span>
            <select name="idEmpresa" value={form.idEmpresa} onChange={handleChange} required>
              <option value="">Selecciona una empresa…</option>
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
              ))}
            </select>
          </label>

          <label className="cal-field">
            <span><CreditCard size={15} /> Plan (texto libre)</span>
            <input
              name="plan"
              value={form.plan}
              onChange={handleChange}
              placeholder="Ej: Plan Pro 6 meses"
              maxLength={150}
            />
          </label>

          <div className="cal-row">
            <label className="cal-field">
              <span><CircleDollarSign size={15} /> Precio (USD)</span>
              <input
                name="precio"
                type="number"
                step="0.01"
                min="0"
                value={form.precio}
                onChange={handleChange}
                placeholder="0.00"
              />
            </label>
            <label className="cal-field">
              <span><CalendarClock size={15} /> Estado</span>
              <select name="estado" value={form.estado} onChange={handleChange}>
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="cal-row">
            <label className="cal-field">
              <span><CalendarDays size={15} /> Fecha inicio</span>
              <input name="fechaInicio" type="date" value={form.fechaInicio} onChange={handleChange} />
            </label>
            <label className="cal-field">
              <span><CalendarClock size={15} /> Fecha vencimiento</span>
              <input name="fechaVencimiento" type="date" value={form.fechaVencimiento} onChange={handleChange} required />
            </label>
          </div>

          <label className="cal-field">
            <span><StickyNote size={15} /> Recordatorio / nota</span>
            <textarea
              name="nota"
              value={form.nota}
              onChange={handleChange}
              rows={3}
              maxLength={500}
              placeholder="Notas internas, acuerdos, observaciones…"
            />
          </label>

          {error && <div className="cal-error">{error}</div>}

          <div className="cal-modal-actions">
            <button type="button" className="cal-btn cal-btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="cal-btn cal-btn-primary" disabled={saving}>
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear pago"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

/* ======================= Tarjeta de pago (vista lista) ======================= */
const PagoCard = ({ pago, onEdit, onEstado, onDelete, onNotificar, notificando }) => {
  const info = estadoInfo(pago.estado)
  const Icon = info.icon
  const dias = pago.diasRestantes
  const vencido = dias != null && dias < 0 && pago.estado === "PENDIENTE"

  let badgeVenc = null
  if (pago.estado === "PENDIENTE") {
    if (vencido) badgeVenc = { txt: `Venció hace ${Math.abs(dias)} d`, cls: "venc-rojo" }
    else if (pago.porVencer) badgeVenc = { txt: dias === 0 ? "¡Vence hoy!" : `Vence en ${dias} d`, cls: "venc-amarillo" }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`cal-card ${pago.porVencer ? "pulsing" : ""}`}
      style={{ "--estado-color": info.color }}
    >
      <div className="cal-card-bar" />
      <div className="cal-card-body">
        <div className="cal-card-top">
          <div className="cal-card-empresa">
            <Building2 size={16} />
            <span>{pago.nombreEmpresa}</span>
          </div>
          <span className="cal-estado-chip" style={{ background: info.color }}>
            <Icon size={13} /> {info.label}
          </span>
        </div>

        <div className="cal-card-plan">{pago.plan || "Sin plan especificado"}</div>

        <div className="cal-card-meta">
          <span><CircleDollarSign size={14} /> {fmtMoneda(pago.precio)}</span>
          <span><CalendarClock size={14} /> Vence: {fmtFecha(pago.fechaVencimiento)}</span>
          {badgeVenc && <span className={`cal-venc-badge ${badgeVenc.cls}`}><AlertTriangle size={12} /> {badgeVenc.txt}</span>}
          {pago.notificado && <span className="cal-notif"><Mail size={12} /> Avisado</span>}
        </div>

        {pago.nota && <div className="cal-card-nota"><StickyNote size={13} /> {pago.nota}</div>}

        {/* Botón Notificar: solo visible cuando el servicio está dentro de los días de aviso */}
        {pago.porVencer && (
          <button
            className="cal-notify-btn"
            onClick={() => onNotificar(pago)}
            disabled={notificando}
            title="Enviar recordatorio ahora (correo / WhatsApp)"
          >
            {notificando ? (
              <><span className="cal-mini-spinner" /> Enviando…</>
            ) : (
              <><Bell size={15} /> Notificar ahora</>
            )}
          </button>
        )}

        <div className="cal-card-actions">
          <div className="cal-estado-quick">
            {Object.entries(ESTADOS).map(([k, v]) => (
              <button
                key={k}
                title={`Marcar como ${v.label}`}
                className={`cal-quick-dot ${pago.estado === k ? "active" : ""}`}
                style={{ "--dot": v.color }}
                onClick={() => onEstado(pago, k)}
              />
            ))}
          </div>
          <div className="cal-card-btns">
            <button className="cal-icon-btn" title="Editar" onClick={() => onEdit(pago)}><Edit3 size={16} /></button>
            <button className="cal-icon-btn danger" title="Eliminar" onClick={() => onDelete(pago)}><Trash2 size={16} /></button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

/* ============================== Página ============================== */
const Calendario = () => {
  const [pagos, setPagos] = useState([])
  const [empresas, setEmpresas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [vista, setVista] = useState("calendario") // "calendario" | "lista"
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [filtroEstado, setFiltroEstado] = useState("TODOS")
  const [toast, setToast] = useState(null)
  const [notificandoId, setNotificandoId] = useState(null)

  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const cargar = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const [pagosData, empresasData] = await Promise.all([getPagos(), getEmpresas()])
      setPagos(Array.isArray(pagosData) ? pagosData : [])
      setEmpresas(Array.isArray(empresasData) ? empresasData : [])
    } catch (e) {
      setError(e.message || "Error al cargar el calendario.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    cargar()
    // Al abrir el calendario se dispara la revisión de vencimientos (recordatorios n8n)
    revisarVencimientos().then((r) => {
      if (r && r.recordatoriosEnviados > 0) {
        showToast(`📧 ${r.recordatoriosEnviados} recordatorio(s) de vencimiento enviado(s).`)
      }
    })
  }, [cargar])

  /* ----- Estadísticas ----- */
  const stats = useMemo(() => {
    const s = { total: pagos.length, pendientes: 0, renovados: 0, noRenovo: 0, porVencer: 0 }
    pagos.forEach((p) => {
      if (p.estado === "PENDIENTE") s.pendientes++
      else if (p.estado === "RENOVADO") s.renovados++
      else if (p.estado === "NO_RENOVO") s.noRenovo++
      if (p.porVencer) s.porVencer++
    })
    return s
  }, [pagos])

  /* ----- Filtro ----- */
  const pagosFiltrados = useMemo(() => {
    let arr = pagos
    if (filtroEstado === "POR_VENCER") arr = pagos.filter((p) => p.porVencer)
    else if (filtroEstado !== "TODOS") arr = pagos.filter((p) => p.estado === filtroEstado)
    return [...arr].sort((a, b) => (a.fechaVencimiento || "").localeCompare(b.fechaVencimiento || ""))
  }, [pagos, filtroEstado])

  const proximosAVencer = useMemo(
    () =>
      pagos
        .filter((p) => p.porVencer)
        .sort((a, b) => (a.diasRestantes ?? 999) - (b.diasRestantes ?? 999)),
    [pagos]
  )

  /* ----- Eventos del calendario ----- */
  const eventos = useMemo(
    () =>
      pagosFiltrados
        .filter((p) => p.fechaVencimiento)
        .map((p) => {
          const d = toDate(p.fechaVencimiento)
          return {
            id: p.id,
            title: `${p.nombreEmpresa}${p.plan ? " · " + p.plan : ""}`,
            start: d,
            end: d,
            allDay: true,
            resource: p,
          }
        }),
    [pagosFiltrados]
  )

  const eventPropGetter = useCallback((event) => {
    const p = event.resource
    const info = estadoInfo(p.estado)
    return {
      style: {
        backgroundColor: info.color,
        borderRadius: "6px",
        border: "none",
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.72rem",
        padding: "1px 6px",
        boxShadow: p.porVencer ? "0 0 0 2px rgba(239,68,68,.5)" : "none",
      },
    }
  }, [])

  /* ----- Acciones ----- */
  const handleSave = async (payload, id) => {
    if (id) {
      await updatePago(id, payload)
      showToast("✅ Pago actualizado.")
    } else {
      await createPago(payload)
      showToast("✅ Pago creado.")
    }
    setModalOpen(false)
    setEditing(null)
    await cargar()
  }

  const handleEstado = async (pago, estado) => {
    if (pago.estado === estado) return
    try {
      await updateEstadoPago(pago.id, estado)
      setPagos((prev) => prev.map((p) => (p.id === pago.id ? { ...p, estado } : p)))
      showToast(`Estado: ${estadoInfo(estado).label}`)
      cargar()
    } catch (e) {
      showToast("⚠️ No se pudo cambiar el estado.")
    }
  }

  const handleNotificar = async (pago) => {
    if (notificandoId) return
    try {
      setNotificandoId(pago.id)
      await notificarPago(pago.id)
      setPagos((prev) => prev.map((p) => (p.id === pago.id ? { ...p, notificado: true } : p)))
      showToast(`📨 Recordatorio enviado a ${pago.nombreEmpresa}.`)
    } catch (e) {
      showToast(`⚠️ ${e.message || "No se pudo enviar el recordatorio."}`)
    } finally {
      setNotificandoId(null)
    }
  }

  const handleDelete = async (pago) => {
    if (!window.confirm(`¿Eliminar el pago de "${pago.nombreEmpresa}"? Esta acción no se puede deshacer.`)) return
    try {
      await deletePago(pago.id)
      setPagos((prev) => prev.filter((p) => p.id !== pago.id))
      showToast("🗑️ Pago eliminado.")
    } catch (e) {
      showToast("⚠️ No se pudo eliminar.")
    }
  }

  const openNuevo = () => { setEditing(null); setModalOpen(true) }
  const openEditar = (pago) => { setEditing(pago); setModalOpen(true) }
  const onSelectEvent = (event) => openEditar(event.resource)

  const statCards = [
    { key: "TODOS", label: "Total", value: stats.total, color: "#920606", icon: List },
    { key: "POR_VENCER", label: "Por vencer", value: stats.porVencer, color: "#f97316", icon: AlertTriangle },
    { key: "PENDIENTE", label: "Pendientes", value: stats.pendientes, color: "#f59e0b", icon: Clock },
    { key: "RENOVADO", label: "Renovados", value: stats.renovados, color: "#10b981", icon: CheckCircle2 },
    { key: "NO_RENOVO", label: "No renovó", value: stats.noRenovo, color: "#ef4444", icon: XCircle },
  ]

  return (
    <div className="cal-page">
      {/* Encabezado */}
      <div className="cal-header">
        <div className="cal-title">
          <CalendarDays size={26} />
          <div>
            <h1>Calendario de Pagos</h1>
            <p>Control de cobros y renovaciones por empresa</p>
          </div>
        </div>
        <div className="cal-header-actions">
          <div className="cal-view-toggle">
            <button className={vista === "calendario" ? "active" : ""} onClick={() => setVista("calendario")}>
              <CalendarDays size={16} /> Calendario
            </button>
            <button className={vista === "lista" ? "active" : ""} onClick={() => setVista("lista")}>
              <ListIcon size={16} /> Lista
            </button>
          </div>
          <button className="cal-btn cal-btn-primary" onClick={openNuevo}>
            <Plus size={18} /> Nuevo pago
          </button>
        </div>
      </div>

      {/* Tarjetas de estadística / filtros */}
      <div className="cal-stats">
        {statCards.map((c) => {
          const Icon = c.icon || List
          const active = filtroEstado === c.key
          return (
            <motion.button
              key={c.key}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              className={`cal-stat ${active ? "active" : ""}`}
              style={{ "--stat-color": c.color }}
              onClick={() => setFiltroEstado(active && c.key !== "TODOS" ? "TODOS" : c.key)}
            >
              <span className="cal-stat-icon"><Icon size={18} /></span>
              <span className="cal-stat-value">{c.value}</span>
              <span className="cal-stat-label">{c.label}</span>
            </motion.button>
          )
        })}
      </div>

      {/* Banda de próximos a vencer */}
      {proximosAVencer.length > 0 && (
        <div className="cal-alert-strip">
          <AlertTriangle size={16} />
          <span>
            <strong>{proximosAVencer.length}</strong> servicio(s) por vencer en los próximos días:&nbsp;
            {proximosAVencer.slice(0, 4).map((p, i) => (
              <span key={p.id} className="cal-alert-chip">
                {p.nombreEmpresa} ({p.diasRestantes === 0 ? "hoy" : `${p.diasRestantes}d`})
                {i < Math.min(proximosAVencer.length, 4) - 1 ? "" : ""}
              </span>
            ))}
            {proximosAVencer.length > 4 && <span className="cal-alert-more">+{proximosAVencer.length - 4} más</span>}
          </span>
        </div>
      )}

      {error && <div className="cal-error cal-error-block">{error}</div>}

      {loading ? (
        <div className="cal-loading"><div className="cal-spinner" /> Cargando calendario…</div>
      ) : vista === "calendario" ? (
        <div className="cal-calendar-wrap">
          <Calendar
            localizer={localizer}
            culture="es"
            events={eventos}
            startAccessor="start"
            endAccessor="end"
            views={["month", "agenda"]}
            popup
            eventPropGetter={eventPropGetter}
            onSelectEvent={onSelectEvent}
            messages={{
              next: "Sig.",
              previous: "Ant.",
              today: "Hoy",
              month: "Mes",
              agenda: "Agenda",
              date: "Fecha",
              time: "Hora",
              event: "Pago",
              noEventsInRange: "No hay vencimientos en este rango.",
              showMore: (total) => `+${total} más`,
            }}
            style={{ height: 620 }}
          />
        </div>
      ) : (
        <div className="cal-list">
          <AnimatePresence>
            {pagosFiltrados.length === 0 ? (
              <div className="cal-empty">
                <CalendarDays size={48} />
                <p>No hay pagos registrados {filtroEstado !== "TODOS" ? "con este filtro" : ""}.</p>
                <button className="cal-btn cal-btn-primary" onClick={openNuevo}><Plus size={16} /> Crear el primero</button>
              </div>
            ) : (
              pagosFiltrados.map((pago) => (
                <PagoCard
                  key={pago.id}
                  pago={pago}
                  onEdit={openEditar}
                  onEstado={handleEstado}
                  onDelete={handleDelete}
                  onNotificar={handleNotificar}
                  notificando={notificandoId === pago.id}
                />
              ))
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <PagoModal
            pago={editing}
            empresas={empresas}
            onClose={() => { setModalOpen(false); setEditing(null) }}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className="cal-toast"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Ícono "List" para la tarjeta Total (evita import duplicado con react-big-calendar List)
function List(props) {
  return <ListIcon {...props} />
}

export default Calendario
